import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "stores/Stores";
import { observer } from "mobx-react-lite";
import { Board } from "../msTeamsUI/components/Board/Board";
import { TBoardLanes } from "../msTeamsUI/components/Board/BoardLane";
import { TUsers } from "../msTeamsUI/types/types";
import { DateRange } from "shared/models/DateRange";
import { Loader } from "ecom-teams-apps-core";
import pl from "shared/messages/pl";
import styles from "./tasksBoard.module.scss";
import { Alert, Flex, FlexItem } from "@fluentui/react-northstar";
import MobileBoard from "msTeamsUI/components/Board/MobileBoard";
import { runInAction } from "mobx";
import { constants } from "shared/constants";
import { CloseTaskEvent } from "appilication-insights/events/CloseTask/CloseTaskEvent";
import { CloseTaskEventSource } from "appilication-insights/events/CloseTask/CloseTaskEventSource";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export interface TaskBoardProps {
    chosenTaskId: number;
    dateRange: DateRange;
    isMobile?: boolean;

    onShowTaskDetails: (taskId: number) => void;
    onTaskClosed?: (source: CloseTaskEventSource) => void;
}

const TasksBoard: React.FC<TaskBoardProps> = observer((props: TaskBoardProps) => {
    const { taskStatusesStore, tasksStore } = useStore();
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const appInsightsContext = useAppInsightsContext();

    const loadBoardData = useCallback(async () => {
        await tasksStore.loadTasks(props.dateRange);
    }, [tasksStore, props.dateRange]);

    useEffect(() => {
        loadBoardData();
    }, [loadBoardData]);

    const getUsers = () => {
        const users: TUsers = {};

        const uniqueNames = tasksStore.tasks
            .filter((t) => t.assignee)
            .map((t) => t.assignee.fullName || t.assignee.userName)
            .filter((x, i, a) => a.indexOf(x) === i);
        uniqueNames.forEach((n) => {
            users[n] = { name: n };
        });

        return users;
    };

    const getBoardLanes = () => {
        const boardLanes: TBoardLanes = {};

        taskStatusesStore.taskStatuses["TASK_STATUS"]
            .slice()
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .forEach((s) => {
                boardLanes[s.code] = { title: s.description };
            });

        return boardLanes;
    };

    const renderLoader = () => {
        return (
            <div className={styles.loaderWrapper}>
                <Loader text={pl.taskBoard.loading} />
            </div>
        );
    };

    if (!taskStatusesStore.loadTasksStatusesRequestStatus.isDone || !tasksStore.boardItemsLoaded) {
        return renderLoader();
    }

    const sharedBoardProps = {
        chosenTaskId: props.chosenTaskId,
        onShowTaskDetails: (taskId: number) => {
            props.onShowTaskDetails(taskId);
            tasksStore.resetAllRequestStatuses();
        },
        onChangeTaskStatus: async (taskId: number, previousTaskId: number, status: string) => {
            const newStatus = taskStatusesStore.taskStatuses["TASK_STATUS"].find((x) => x.code === status);
            if (newStatus) {
                const successfullyUpdatedTaskStatus = await tasksStore.updateTaskStatus(
                    taskId,
                    previousTaskId,
                    status,
                    newStatus?.id
                );
                if (successfullyUpdatedTaskStatus === false) {
                    tasksStore.loadTasks(props.dateRange);
                } else {
                    if (tasksStore.selectedTask) {
                        runInAction(() => {
                            tasksStore.selectedTask!.status = tasksStore.tasks.find(
                                (x) => x.id === tasksStore.selectedTask!.id
                            )?.status!;
                        });
                    }
                    if (props.onTaskClosed && newStatus.code === constants.taskStatuses.closed) {
                        props.onTaskClosed(CloseTaskEventSource.Dashboard);
                    }
                }
            }

            return false;
        },
        users: getUsers(),
        lanes: getBoardLanes(),
        items: tasksStore.filteredBoardItems,
    };

    return (
        <Flex column className={styles.boardWrapper} padding={props.isMobile ? undefined : "padding.medium"}>
            {isAlertVisible && (
                <FlexItem align="start">
                    <Alert
                        danger
                        dismissible
                        onVisibleChange={() => setIsAlertVisible(false)}
                        dismissAction={{
                            "aria-label": pl.common.close,
                        }}
                        content={tasksStore.updateTaskStatusRequestStatus.error}
                    />
                </FlexItem>
            )}
            {
                // TODO: Switch boards based on props.isMobile flag (EHTA-187)
                props.isMobile ? <MobileBoard {...sharedBoardProps} /> : <Board {...sharedBoardProps} />
            }
        </Flex>
    );
});

export default TasksBoard;
